import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Shield, Loader2, LogOut, CalendarCheck, IndianRupee, BedDouble, TrendingUp,
  Plus, Pencil, Trash2, Download, ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { RoomFormModal } from "@/components/site/RoomFormModal";
import { ManualBookingModal } from "@/components/site/ManualBookingModal";
import { AdminGallery } from "@/components/site/AdminGallery";
import { AdminOffers } from "@/components/site/AdminOffers";
import { AdminRates } from "@/components/site/AdminRates";
import { toast } from "sonner";
import api, { formatApiErrorDetail, imgUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { GoogleButton } from "@/components/site/GoogleButton";

const STATUSES = ["All", "Confirmed", "Checked-In", "Cancelled"];
const statusColor = {
  Confirmed: "bg-secondary/15 text-secondary border-secondary/30",
  "Checked-In": "bg-primary/15 text-primary border-primary/30",
  Cancelled: "bg-destructive/15 text-destructive border-destructive/30",
};

function Login({ onLogin, deniedUser, onSignOut }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/login", { email, password });
      onLogin();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111827] flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-sm bg-card rounded-2xl p-8 shadow-2xl">
        <button onClick={() => navigate("/")} className="text-xs text-muted-foreground flex items-center gap-1 mb-4 hover:text-primary">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to site
        </button>
        <div className="w-12 h-12 rounded-xl bg-primary/15 text-primary flex items-center justify-center"><Shield className="h-6 w-6" /></div>
        <h1 className="mt-4 font-serif text-2xl font-semibold">Admin Portal</h1>
        <p className="text-sm text-muted-foreground">Hotel Vijayshree Inn management</p>
        {deniedUser && (
          <div className="mt-4 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive" data-testid="admin-access-denied">
            {deniedUser.email} is not an admin account.
            <button onClick={onSignOut} className="underline ml-1">Sign out</button>
          </div>
        )}
        <GoogleButton returnPath="/admin" label="Sign in with Google" className="mt-6 w-full h-11" testId="admin-google-signin-button" />
        <div className="my-4 flex items-center gap-3 text-[11px] text-muted-foreground"><span className="h-px flex-1 bg-border" />or use password<span className="h-px flex-1 bg-border" /></div>
        <form onSubmit={submit} className="space-y-4">
          <div><Label className="text-xs">Email</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" /></div>
          <div><Label className="text-xs">Password</Label>
            <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" /></div>
          <Button type="submit" disabled={loading} className="w-full h-11 bg-primary hover:bg-primary/90 gap-2" data-testid="admin-login-button">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign In
          </Button>
        </form>
      </motion.div>
    </div>
  );
}

const StatCard = ({ icon, label, value, i }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
    className="rounded-2xl border border-border bg-card p-5">
    <div className="flex items-center justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">{icon}</span>
    </div>
    <div className="mt-3 font-mono text-2xl font-bold">{value}</div>
  </motion.div>
);

export default function Admin() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [filter, setFilter] = useState("All");
  const [roomModal, setRoomModal] = useState({ open: false, room: null });
  const [manualOpen, setManualOpen] = useState(false);

  const loadData = useCallback(async () => {
    const [s, b, r] = await Promise.all([
      api.get("/admin/stats"), api.get("/bookings"), api.get("/rooms"),
    ]);
    setStats(s.data); setBookings(b.data); setRooms(r.data);
  }, []);

  useEffect(() => {
    if (auth.loading) return;
    if (auth.user?.role === "admin") {
      setUser(auth.user);
      loadData().finally(() => setChecking(false));
      return;
    }
    setUser(null);
    setChecking(false);
  }, [auth.loading, auth.user, loadData]);

  const onLogin = async (u) => { const { data } = await api.get("/auth/me"); auth.setUser(data); setUser(data); await loadData(); };
  const logout = async () => { await auth.logout(); setUser(null); };

  const updateStatus = async (ref, status) => {
    await api.put(`/bookings/${ref}/status`, { status });
    toast.success(`Marked ${status}`);
    loadData();
  };

  const deleteRoom = async (room_id) => {
    if (!window.confirm("Delete this room?")) return;
    await api.delete(`/rooms/${room_id}`);
    toast.success("Room deleted");
    loadData();
  };

  const exportCsv = () => {
    const headers = ["Reference", "Guest", "Email", "Phone", "Room", "CheckIn", "CheckOut", "Nights", "Guests", "Total", "Status"];
    const rows = bookings.map((b) => [b.booking_ref, b.guest_name, b.guest_email, b.guest_phone, b.room_name, b.check_in, b.check_out, b.nights, b.guests, b.total, b.status]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "vijayshree-bookings.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  if (checking) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (!user) return <Login onLogin={onLogin} deniedUser={auth.user && auth.user.role !== "admin" ? auth.user : null} onSignOut={logout} />;

  const shown = filter === "All" ? bookings : bookings.filter((b) => b.status === filter);

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-dashboard-view">
      <header className="sticky top-0 z-40 bg-background border-b border-border">
        <div className="px-4 sm:px-6 lg:px-10 max-w-7xl mx-auto h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <span className="font-serif font-semibold">Vijayshree Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}>View Site</Button>
            <Button variant="outline" size="sm" onClick={logout} className="gap-1.5" data-testid="admin-logout-button"><LogOut className="h-4 w-4" /> Logout</Button>
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-6 lg:px-10 max-w-7xl mx-auto py-8 space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard i={0} icon={<CalendarCheck className="h-5 w-5" />} label="Total Bookings" value={stats?.total_bookings ?? 0} />
          <StatCard i={1} icon={<IndianRupee className="h-5 w-5" />} label="Revenue" value={`₹${(stats?.total_revenue ?? 0).toLocaleString("en-IN")}`} />
          <StatCard i={2} icon={<TrendingUp className="h-5 w-5" />} label="Upcoming Check-ins" value={stats?.upcoming_checkins ?? 0} />
          <StatCard i={3} icon={<BedDouble className="h-5 w-5" />} label="Rooms" value={stats?.total_rooms ?? 0} />
        </div>

        {/* Bookings */}
        <section className="rounded-2xl border border-border bg-card p-5 lg:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-serif text-xl font-semibold">Bookings</h2>
            <div className="flex items-center gap-2">
              <div className="flex gap-1 rounded-lg bg-muted p-1">
                {STATUSES.map((s) => (
                  <button key={s} onClick={() => setFilter(s)} data-testid={`booking-filter-${s.toLowerCase()}`}
                    className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${filter === s ? "bg-background shadow text-primary" : "text-muted-foreground"}`}>{s}</button>
                ))}
              </div>
              <Button size="sm" onClick={() => setManualOpen(true)} className="gap-1.5 bg-primary hover:bg-primary/90" data-testid="manual-booking-button"><Plus className="h-4 w-4" /> Add Booking</Button>
              <Button size="sm" variant="outline" onClick={exportCsv} className="gap-1.5" data-testid="export-csv-button"><Download className="h-4 w-4" /> CSV</Button>
            </div>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="py-2 pr-4 font-medium">Ref</th>
                  <th className="py-2 pr-4 font-medium">Guest</th>
                  <th className="py-2 pr-4 font-medium">Room</th>
                  <th className="py-2 pr-4 font-medium">Dates</th>
                  <th className="py-2 pr-4 font-medium">Total</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">No bookings yet.</td></tr>}
                {shown.map((b) => (
                  <tr key={b.booking_ref} className="border-b border-border/60" data-testid="admin-booking-row">
                    <td className="py-3 pr-4 font-mono text-xs">{b.booking_ref}</td>
                    <td className="py-3 pr-4"><div className="font-medium">{b.guest_name}</div><div className="text-xs text-muted-foreground">{b.guest_phone}</div></td>
                    <td className="py-3 pr-4">{b.room_name}</td>
                    <td className="py-3 pr-4 text-xs">{b.check_in} → {b.check_out}<div className="text-muted-foreground">{b.nights}n · {b.guests}g</div></td>
                    <td className="py-3 pr-4 font-mono font-medium">₹{b.total}</td>
                    <td className="py-3 pr-4"><Badge variant="outline" className={statusColor[b.status]}>{b.status}</Badge></td>
                    <td className="py-3">
                      <select value={b.status} onChange={(e) => updateStatus(b.booking_ref, e.target.value)}
                        data-testid={`booking-status-select-${b.booking_ref}`}
                        className="text-xs border border-input rounded-md px-2 py-1 bg-background">
                        {["Confirmed", "Checked-In", "Cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Rooms */}
        <section className="rounded-2xl border border-border bg-card p-5 lg:p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-xl font-semibold">Room Manager</h2>
            <Button size="sm" onClick={() => setRoomModal({ open: true, room: null })} className="gap-1.5 bg-primary hover:bg-primary/90" data-testid="admin-add-room-button">
              <Plus className="h-4 w-4" /> Add Room
            </Button>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {rooms.map((r) => (
              <div key={r.room_id} className="rounded-xl border border-border overflow-hidden" data-testid={`admin-room-${r.room_id}`}>
                <div className="h-28 bg-muted"><img src={imgUrl(r.image)} alt={r.name} className="w-full h-full object-cover" /></div>
                <div className="p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{r.name}</span>
                    {!r.is_available && <Badge variant="destructive" className="text-[10px]">Off</Badge>}
                  </div>
                  <div className="font-mono text-primary font-semibold mt-0.5">₹{r.price_per_night}<span className="text-xs text-muted-foreground">/night</span></div>
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" variant="outline" className="flex-1 gap-1 h-8" onClick={() => setRoomModal({ open: true, room: r })} data-testid={`edit-room-${r.room_id}`}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                    <Button size="sm" variant="outline" className="h-8 text-destructive hover:text-destructive" onClick={() => deleteRoom(r.room_id)} data-testid={`delete-room-${r.room_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <AdminGallery />
        <AdminRates rooms={rooms} />
        <AdminOffers />
      </main>

      {roomModal.open && (
        <RoomFormModal open={roomModal.open} room={roomModal.room}
          onClose={() => setRoomModal({ open: false, room: null })} onSaved={loadData} />
      )}
      {manualOpen && (
        <ManualBookingModal rooms={rooms} onClose={() => setManualOpen(false)} onSaved={loadData} />
      )}
    </div>
  );
}
